import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Heart, Plus, Package, ArrowLeft, TrendingUp } from "lucide-react";
import { api, formatApiError, API_BASE } from "@/lib/apiClient";
import { getGuestToken } from "@/lib/guestToken";

const STATUS_LABEL = {
  gathering_interest: { l: "Gathering Interest", c: "bg-amber-100 text-amber-800" },
  under_consideration: { l: "Under Consideration", c: "bg-sky-100 text-sky-800" },
  sourcing: { l: "Sourcing", c: "bg-blue-100 text-blue-800" },
  ordered: { l: "Ordered", c: "bg-purple-100 text-purple-800" },
  available_in_store: { l: "Available in Store", c: "bg-emerald-100 text-emerald-800" },
  currently_unavailable: { l: "Currently Unavailable", c: "bg-slate-200 text-slate-700" },
};

export default function RequestedProductDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [liked, setLiked] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/wishlist/products/slug/${slug}`);
        setP(data);
      } catch (e) {
        if (e?.response?.status === 404) setNotFound(true);
        else toast.error(formatApiError(e));
      }
    })();
    try {
      const m = JSON.parse(localStorage.getItem("pp_wl_liked") || "{}");
      // We'll know after load
      setLiked(false); // reset
      void m;
    } catch { /* ignore */ }
  }, [slug]);

  useEffect(() => {
    if (!p) return;
    try {
      const m = JSON.parse(localStorage.getItem("pp_wl_liked") || "{}");
      setLiked(!!m[p.id]);
    } catch { /* ignore */ }
  }, [p]);

  const toggleLike = async () => {
    try {
      const { data } = await api.post(`/wishlist/products/${p.id}/like`, { guest_token: getGuestToken() });
      setP({ ...p, like_count: data.like_count });
      setLiked(data.liked);
      const m = JSON.parse(localStorage.getItem("pp_wl_liked") || "{}");
      m[p.id] = data.liked; localStorage.setItem("pp_wl_liked", JSON.stringify(m));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const requestToo = async () => {
    try {
      const { data } = await api.post(`/wishlist/products/${p.id}/request-too`, { guest_token: getGuestToken() });
      toast.success(`Wish added! Your tracker: ${data.tracking_code}`, { duration: 8000 });
      try {
        const stored = JSON.parse(localStorage.getItem("pp_my_requests") || "[]");
        const entry = { ref: data.public_ref, code: data.tracking_code, name: p.name, at: new Date().toISOString() };
        localStorage.setItem("pp_my_requests", JSON.stringify([entry, ...stored.filter(x => x.ref !== data.public_ref)].slice(0, 10)));
      } catch { /* ignore */ }
      setP({ ...p, request_count: p.request_count + 1 });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (notFound) return (
    <div className="min-h-screen bg-[#FFF8F0] grid place-items-center px-4" data-testid="product-not-found">
      <div className="text-center max-w-sm">
        <h1 className="font-display text-3xl mb-2">Product not available</h1>
        <p className="text-sm text-brand-charcoal/60 mb-5">This product may have been merged, hidden, or isn't live yet.</p>
        <Link to="/requested-products" className="btn-pill btn-dark text-xs">Back to Wishlist</Link>
      </div>
    </div>
  );
  if (!p) return <div className="min-h-screen bg-[#FFF8F0] text-center text-brand-charcoal/50 py-20">Loading…</div>;

  const sl = STATUS_LABEL[p.public_status] || STATUS_LABEL.gathering_interest;
  const imgUrl = p.image_path ? `${API_BASE}/wishlist/images/${p.image_path}` : null;

  return (
    <div className="min-h-screen bg-[#FFF8F0]" data-testid="product-detail-page">
      <div className="max-w-4xl mx-auto px-4 py-10">
        <Link to="/requested-products" className="text-xs text-brand-charcoal/60 hover:text-brand-charcoal inline-flex items-center gap-1 mb-6"><ArrowLeft size={12} /> Back to Wishlist</Link>
        <div className="grid md:grid-cols-2 gap-6 bg-white rounded-3xl border border-brand-line overflow-hidden">
          <div className="aspect-square bg-[#FEED02]/10 grid place-items-center overflow-hidden">
            {imgUrl ? <img src={imgUrl} alt={p.name} className="w-full h-full object-cover" /> : <Package size={60} className="text-brand-charcoal/25" />}
          </div>
          <div className="p-6 flex flex-col">
            <span className={`self-start px-2.5 py-1 rounded-full text-[10px] font-medium uppercase tracking-widest mb-3 ${sl.c}`} data-testid="product-status">{sl.l}</span>
            <h1 className="font-display text-4xl leading-[1.05] mb-2" data-testid="product-name">{p.name}</h1>
            {(p.brand || p.variant) && <div className="text-sm text-brand-charcoal/60 mb-1">{p.brand}{p.brand && p.variant ? " · " : ""}{p.variant}</div>}
            <div className="text-xs text-brand-charcoal/50 mb-5">{p.category}{p.size ? ` · ${p.size}` : ""}</div>
            {p.description && <p className="text-sm text-brand-charcoal/75 mb-5">{p.description}</p>}
            <div className="flex items-center gap-5 mb-6 text-sm">
              <div><strong className="font-display text-3xl">{p.request_count}</strong><div className="text-[10px] uppercase tracking-widest text-brand-charcoal/50 flex items-center gap-1"><TrendingUp size={10} /> People want this</div></div>
              <div><strong className="font-display text-3xl">{p.like_count}</strong><div className="text-[10px] uppercase tracking-widest text-brand-charcoal/50 flex items-center gap-1"><Heart size={10} /> Likes</div></div>
            </div>
            <div className="mt-auto rounded-xl bg-[#FEED02]/20 border border-[#FEED02] p-4 mb-4">
              <p className="text-sm text-brand-charcoal/80">Want Pop Panda to stock this? Add your request and help us understand the demand.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={requestToo} className="btn-pill btn-dark text-xs flex-1" data-testid="btn-request-too"><Plus size={11} /> Request This Too</button>
              <button onClick={toggleLike} className={`btn-pill text-xs ${liked ? "bg-red-100 text-red-600 border-red-200" : "btn-ghost"}`} data-testid="btn-like"><Heart size={11} fill={liked ? "currentColor" : "none"} /> {p.like_count}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
